import { useEffect, useRef } from "react";

const COLS = ["#FF8A65", "#FFB08A", "#FFD3B8", "#F05A24", "#E65100", "#FFE0CC"];

export const PixelField = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const parent = canvas.parentElement;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, dots = [], raf;
    const mouse = { x: -9999, y: -9999, active: false };
    let hoverStr = 0;

    const build = () => {
      const rect = parent.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const N = w < 768 ? 900 : 2400;
      dots = [];
      for (let i = 0; i < N; i++) {
        dots.push({
          x: Math.random() * w,
          y: Math.random() * h,
          s: 1.2 + Math.random() * 2.4,
          ph: Math.random() * Math.PI * 2,
          tw: 0.4 + Math.random() * 1.4,
          drift: 0.15 + Math.random() * 0.5,
          col: COLS[(Math.random() * COLS.length) | 0],
          base: 0.14 + Math.random() * 0.42,
        });
      }
    };

    const draw = (now) => {
      ctx.clearRect(0, 0, w, h);
      const t = now;
      hoverStr += ((mouse.active ? 1 : 0) - hoverStr) * 0.06;
      for (const d of dots) {
        const flick = 0.65 + 0.35 * Math.sin(t * 0.0009 * d.tw + d.ph);
        const x = d.x + Math.sin(t * 0.0004 * d.drift + d.ph) * 9;
        const y = d.y + Math.cos(t * 0.0005 * d.drift + d.ph * 1.7) * 7;
        let alpha = d.base * flick;
        let size = d.s;
        if (hoverStr > 0.01) {
          const dx = x - mouse.x;
          const dy = y - mouse.y;
          const dist = Math.hypot(dx, dy);
          if (dist < 170) {
            const k = (1 - dist / 170) * hoverStr;
            alpha = Math.min(1, alpha + k * 0.85);
            size = d.s * (1 + k);
          }
        }
        ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
        ctx.fillStyle = d.col;
        ctx.fillRect(x, y, size, size);
      }
      ctx.globalAlpha = 1;
      if (!reduced) raf = requestAnimationFrame(draw);
    };

    const onMove = (e) => {
      const r = parent.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
      mouse.active = true;
    };
    const onLeave = () => { mouse.active = false; };

    parent.addEventListener("mousemove", onMove);
    parent.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", build);
    build();
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      parent.removeEventListener("mousemove", onMove);
      parent.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", build);
    };
  }, []);

  return <canvas ref={ref} data-testid="pixel-field-canvas" className="absolute inset-0 pointer-events-none" aria-hidden="true" />;
};
